import { useRef, useState, type KeyboardEvent } from "react";
import type { ReviewShipmentRow } from "./data";

// Human labels for the 10 required fields the parser flags in `missingRequired`
// (hassett-keyme-ingest/src/parser.ts REQUIRED_FIELDS). These are the only
// fields surface-by-exception promotes to the card; everything else stays
// behind the editor ("Fix something").
const REQUIRED_FIELD_LABELS: Record<string, string> = {
  poNumber: "PO #",
  keyMeId: "KeyMe ID",
  task: "Task",
  siteName: "Site name",
  address1: "Address",
  city: "City",
  state: "State",
  zip: "ZIP",
  latestDate: "Latest date",
  scheduleUrl: "Schedule link"
};

function prettifyKey(key: string): string {
  return (
    REQUIRED_FIELD_LABELS[key] ??
    key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())
  );
}

function familyLabel(row: ReviewShipmentRow): string {
  const src = (row.source || "").toLowerCase();
  if (src.includes("keyme")) return "KeyMe";
  if (src.includes("estes")) return "ESTES";
  if (src.includes("special")) return "Special";
  return row.summary?.customerCode || "Shipment";
}

interface RowView {
  row: ReviewShipmentRow;
  missing: string[];
  confidence: number | null;
  needsYou: boolean;
}

function toRowView(row: ReviewShipmentRow): RowView {
  const job = (row.job && typeof row.job === "object" ? row.job : {}) as Record<string, unknown>;
  const missingRaw = job.missingRequired;
  const missing = Array.isArray(missingRaw) ? missingRaw.map(String) : [];
  const confidence = typeof job.confidence === "number" ? job.confidence : null;
  const heldStatus = /need|hold|held|pending|blocked|review/i.test(row.status || "");
  // "Needs you" = a required field is blank OR the row's own status says it is
  // not clean. Everything else is a clean decision: glance + open + submit.
  const needsYou = missing.length > 0 || heldStatus;
  return { row, missing, confidence, needsYou };
}

function metaLine(row: ReviewShipmentRow): string {
  const s = row.summary;
  const where = [s?.siteName, [s?.city, s?.state].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" · ");
  return [where, s?.poNumber || ""].filter(Boolean).join(" · ");
}

function Card({
  view,
  selected,
  onPick
}: {
  view: RowView;
  selected: boolean;
  onPick: (id: string) => void;
}) {
  const { row, missing, confidence, needsYou } = view;
  const flag = needsYou
    ? missing.length
      ? `⚠ ${missing.length} field${missing.length === 1 ? "" : "s"} need you`
      : "⚠ needs review"
    : "✓ no required field needs you";
  const cta = needsYou
    ? missing.length
      ? `Fix ${missing.length} field${missing.length === 1 ? "" : "s"} →`
      : "Review →"
    : "Review & submit →";

  return (
    <button
      type="button"
      data-decision-card
      className={`decisionCard ${needsYou ? "needsYou" : "clean"}${selected ? " selected" : ""}`}
      onClick={() => onPick(row.id)}
      aria-label={`${familyLabel(row)} ${row.summary?.task || ""} ${row.summary?.siteName || ""}. ${flag}.`}
    >
      <div className="decisionCardHead">
        <span className="decisionFamily">{familyLabel(row)}</span>
        <span className="decisionFlag">{flag}</span>
        {confidence !== null && <span className="decisionConf">conf {confidence}%</span>}
      </div>
      <div className="decisionTitle">{row.summary?.task || row.subject || "Shipment action"}</div>
      <div className="decisionMeta">{metaLine(row)}</div>
      {missing.length > 0 && (
        <div className="decisionExceptions">
          {missing.map((key) => (
            <span className="decisionChip" key={key}>
              {prettifyKey(key)}
            </span>
          ))}
        </div>
      )}
      <div className="decisionCta">{cta}</div>
    </button>
  );
}

export interface DecisionQueueProps {
  shipments: ReviewShipmentRow[];
  selectedId: string;
  onPick: (id: string) => void;
  loading?: boolean;
  error?: string;
  isSample?: boolean;
}

/**
 * Decision-first triage of the review queue. Splits shipments into "needs you"
 * (a required field is blank) and "clean", names the exact missing fields on
 * each card, and routes a click into the existing editor below. This is the
 * Approach-A surface from OTTO_OPERATOR_UX_DESIGN.md: the exception list is the
 * parser's `missingRequired`; the real submit gate stays in the editor.
 */
export function DecisionQueue({
  shipments,
  selectedId,
  onPick,
  loading,
  error,
  isSample
}: DecisionQueueProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusIndex, setFocusIndex] = useState(0);

  const views = shipments.map(toRowView);
  const needs = views.filter((v) => v.needsYou);
  const clean = views.filter((v) => !v.needsYou);
  const order = [...needs, ...clean];

  const focusCard = (index: number) => {
    const cards = containerRef.current?.querySelectorAll<HTMLButtonElement>("[data-decision-card]");
    cards?.[index]?.focus();
  };

  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (order.length === 0) return;
    if (event.key === "j" || event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(order.length - 1, focusIndex + 1);
      setFocusIndex(next);
      focusCard(next);
    } else if (event.key === "k" || event.key === "ArrowUp") {
      event.preventDefault();
      const prev = Math.max(0, focusIndex - 1);
      setFocusIndex(prev);
      focusCard(prev);
    }
  };

  return (
    <div className="decisionQueue" ref={containerRef} onKeyDown={handleKey}>
      <div className="decisionQueueHead">
        <h3>
          {needs.length
            ? `${needs.length} shipment${needs.length === 1 ? "" : "s"} need you`
            : "Nothing needs you"}
          {clean.length > 0 && <span className="decisionClean"> · {clean.length} clean</span>}
        </h3>
        <div className="decisionQueueHint">
          {isSample && <span className="decisionSample">sample data</span>}
          <span>j / k to move · Enter to open</span>
        </div>
      </div>

      {error && <div className="decisionError" role="alert">{error}</div>}

      {loading && shipments.length === 0 && (
        <div className="decisionSkeleton" aria-hidden="true">
          <span /> <span /> <span />
        </div>
      )}

      {!loading && shipments.length === 0 && !error && (
        <div className="decisionEmpty">Inbox is clear. Nothing needs you right now.</div>
      )}

      {needs.length > 0 && (
        <>
          <div className="decisionGroupLabel needsYou">Needs you</div>
          <div className="decisionCardGrid">
            {needs.map((view) => (
              <Card key={view.row.id} view={view} selected={view.row.id === selectedId} onPick={onPick} />
            ))}
          </div>
        </>
      )}

      {clean.length > 0 && (
        <>
          <div className="decisionGroupLabel clean">Clean — glance and submit</div>
          <div className="decisionCardGrid">
            {clean.map((view) => (
              <Card key={view.row.id} view={view} selected={view.row.id === selectedId} onPick={onPick} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
